/**
 * Two-Factor Verify — step 2 of login.
 *
 * Receives the short-lived `mfa_token` from /pages/Login via router state and
 * lets the user complete the second factor:
 *   - Authenticator app (TOTP)
 *   - Emailed 6-digit code (if enabled)
 *   - One-time backup code (if they've lost their device)
 *
 * On success, the user is routed to their role-based dashboard. If they tick
 * "Remember this browser for 30 days", a trusted-device token is persisted
 * in localStorage and resent on future logins so they skip the 2FA step.
 */
import React, { useEffect, useMemo, useState } from 'react';
import Seo from '@/components/Seo';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Logo } from '@/components/Logo';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useAuth } from '@/lib/auth.jsx';
import api from '@/lib/api';
import { toast } from 'sonner';
import { AlertCircle, Loader2, ArrowLeft, Smartphone, Mail, KeyRound, ShieldCheck } from 'lucide-react';
import { LegalFooter } from '@/components/LegalFooter';

const METHOD_LABEL = { totp: 'Authenticator app', email: 'Email code', backup: 'Backup code' };

export default function TwoFactorVerify() {
    const navigate = useNavigate();
    const location = useLocation();
    const { verify2FA, loading } = useAuth();

    const challenge = location.state || {};
    const methods = useMemo(() => Array.isArray(challenge.methods) ? challenge.methods : [], [challenge.methods]);

    // If we land here without a challenge (e.g. the user refreshed the page),
    // route back to login — the mfa_token is short-lived and lost on refresh.
    useEffect(() => {
        if (!challenge.mfa_token) {
            navigate('/login', { replace: true });
        }
    }, [challenge.mfa_token, navigate]);

    const initialTab = methods.includes('totp') ? 'totp' : (methods.includes('email') ? 'email' : 'backup');
    const [tab, setTab] = useState(initialTab);
    const [code, setCode] = useState('');
    const [remember, setRemember] = useState(true);
    const [error, setError] = useState('');
    const [emailSent, setEmailSent] = useState(false);
    const [sending, setSending] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        setError('');
        const cleaned = (code || '').trim();
        if (!cleaned) {
            setError('Enter your code to continue.');
            return;
        }
        try {
            const u = await verify2FA({
                mfa_token: challenge.mfa_token,
                method: tab,
                code: cleaned,
                remember_device: remember,
                remember_me: !!challenge.remember_me,
                device_label: navigator.userAgent ? navigator.userAgent.slice(0, 60) : 'Web browser',
            });
            toast.success('Welcome back.');
            const next = challenge.next || '';
            if (next) navigate(next, { replace: true });
            else if (u.role === 'instructor') {
                if (u.onboarding_completed === false || (u.instructor_profile && u.instructor_profile.onboarding_completed === false)) {
                    navigate('/instructor/onboarding', { replace: true });
                } else navigate('/instructor/dashboard', { replace: true });
            }
            else if (u.role === 'admin') navigate('/admin/dashboard', { replace: true });
            else if (u.role === 'parent') navigate('/parent/dashboard', { replace: true });
            else if (u.role === 'staff_coach') navigate('/staff/dashboard', { replace: true });
            else navigate('/', { replace: true });
        } catch (err) {
            const msg = err?.response?.data?.detail || err?.message || 'Verification failed.';
            setError(msg);
            toast.error(msg);
        }
    };

    const sendEmailCode = async () => {
        if (!challenge.mfa_token) return;
        setSending(true);
        try {
            const r = await api.post('/auth/2fa/email/send', { mfa_token: challenge.mfa_token });
            if (r.data?.sent) {
                setEmailSent(true);
                toast.success('Code sent. Check your inbox.');
            } else {
                toast.error('Email delivery is not configured — try authenticator or backup code instead.');
            }
        } catch (err) {
            const msg = err?.response?.data?.detail || 'Could not send code.';
            toast.error(msg);
        } finally { setSending(false); }
    };

    if (!challenge.mfa_token) return null; // redirecting

    return (
        <div className="min-h-screen bg-[#f4f7fb] flex flex-col" data-testid="two-factor-verify-page">
            <Seo
                title="Two-factor verification | SplashPad"
                description="Enter your two-factor authentication code to finish logging in to SplashPad."
                canonical="https://splashpad.pro/login"
                noIndex
            />
            <div className="px-4 py-4 sm:px-6">
                <Link to="/" data-testid="two-factor-back-home-link"><Logo /></Link>
            </div>
            <div className="flex flex-1 items-center justify-center px-4 pb-24 sm:pb-12 sm:px-6">
                <Card className="sd-card w-full max-w-md p-6 sm:p-8">
                    <div className="flex items-start gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#00d4ff]/15 text-[#0a1628]" aria-hidden="true">
                            <ShieldCheck className="h-5 w-5" />
                        </div>
                        <div>
                            <h1 className="font-display text-2xl font-bold text-[#0a1628]" data-testid="two-factor-heading">Two-step verification</h1>
                            <p className="mt-1 text-sm text-[#1a3a5c]">
                                Enter the code from your authenticator app{methods.includes('email') ? ' or email' : ''} to finish signing in
                                {challenge.email_masked ? <> for <strong>{challenge.email_masked}</strong></> : ''}.
                            </p>
                        </div>
                    </div>

                    <Tabs value={tab} onValueChange={(v) => { setTab(v); setCode(''); setError(''); }} className="mt-5">
                        <TabsList className="grid w-full grid-cols-3" data-testid="two-factor-tabs">
                            <TabsTrigger value="totp" disabled={!methods.includes('totp')} data-testid="two-factor-tab-totp">
                                <Smartphone className="mr-1 h-4 w-4" /> App
                            </TabsTrigger>
                            <TabsTrigger value="email" disabled={!methods.includes('email')} data-testid="two-factor-tab-email">
                                <Mail className="mr-1 h-4 w-4" /> Email
                            </TabsTrigger>
                            <TabsTrigger value="backup" data-testid="two-factor-tab-backup">
                                <KeyRound className="mr-1 h-4 w-4" /> Backup
                            </TabsTrigger>
                        </TabsList>

                        <form onSubmit={onSubmit} className="mt-4 space-y-4" data-testid="two-factor-form">
                            <TabsContent value="totp" className="space-y-2 m-0">
                                <Label htmlFor="totp-code">6-digit code from your authenticator</Label>
                                <Input
                                    id="totp-code"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    pattern="[0-9]*"
                                    maxLength={6}
                                    value={code}
                                    onChange={(e) => setCode(e.target.value.replace(/\D+/g, '').slice(0, 6))}
                                    className="h-12 text-center text-lg tracking-[0.5em] rounded-xl"
                                    placeholder="••••••"
                                    data-testid="two-factor-totp-input"
                                    autoFocus={tab === 'totp'}
                                />
                                <p className="text-xs text-[#1a3a5c]/70">Open Google Authenticator, 1Password, Authy, or your preferred app and enter the current 6-digit code.</p>
                            </TabsContent>

                            <TabsContent value="email" className="space-y-2 m-0">
                                <div className="flex items-center justify-between gap-2">
                                    <Label htmlFor="email-code">6-digit code from your email</Label>
                                    <Button type="button" variant="outline" size="sm" onClick={sendEmailCode} disabled={sending} data-testid="two-factor-email-send">
                                        {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (emailSent ? 'Resend code' : 'Send code')}
                                    </Button>
                                </div>
                                <Input
                                    id="email-code"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    pattern="[0-9]*"
                                    maxLength={6}
                                    value={code}
                                    onChange={(e) => setCode(e.target.value.replace(/\D+/g, '').slice(0, 6))}
                                    className="h-12 text-center text-lg tracking-[0.5em] rounded-xl"
                                    placeholder="••••••"
                                    data-testid="two-factor-email-input"
                                    autoFocus={tab === 'email'}
                                />
                                <p className="text-xs text-[#1a3a5c]/70">Codes expire after 10 minutes. Tap “Send code” to deliver a fresh one to your account email.</p>
                            </TabsContent>

                            <TabsContent value="backup" className="space-y-2 m-0">
                                <Label htmlFor="backup-code">Backup code</Label>
                                <Input
                                    id="backup-code"
                                    autoCapitalize="characters"
                                    autoCorrect="off"
                                    spellCheck={false}
                                    maxLength={11}
                                    value={code}
                                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                                    className="h-12 text-center tracking-[0.25em] rounded-xl"
                                    placeholder="XXXXX-XXXXX"
                                    data-testid="two-factor-backup-input"
                                    autoFocus={tab === 'backup'}
                                />
                                <p className="text-xs text-[#1a3a5c]/70">Use one of the 8 backup codes you saved when you turned on 2FA. Each code only works once.</p>
                            </TabsContent>

                            <label htmlFor="remember-device" className="flex items-start gap-2 select-none cursor-pointer">
                                <Checkbox id="remember-device" checked={remember} onCheckedChange={(v) => setRemember(!!v)} className="mt-0.5" data-testid="two-factor-remember-device" />
                                <span className="text-sm text-[#1a3a5c]">Trust this browser for 30 days (skip 2FA on next sign-in)</span>
                            </label>

                            {error && (
                                <div role="alert" aria-live="assertive" className="flex items-start gap-2 rounded-xl border border-[#ff6b35]/40 bg-[#ff6b35]/10 px-3 py-2.5 text-sm text-[#0a1628]" data-testid="two-factor-error">
                                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-[#ff6b35]" />
                                    <div className="space-y-1">
                                        <div>{error}</div>
                                        {/* Lockout responses come back as 429 with a "Try again in N minutes" detail.
                                            When that's the case we add a one-line nudge so the user knows
                                            their backup codes won't help either — they need to wait or
                                            contact the platform owner. */}
                                        {/Too many failed attempts/i.test(error) && (
                                            <div className="text-xs text-[#1a3a5c]/80">Backup codes are also paused during a lockout. If you're truly stuck, email <a href="mailto:support@splashpad.pro" className="font-semibold underline">support@splashpad.pro</a>.</div>
                                        )}
                                    </div>
                                </div>
                            )}

                            <Button type="submit" disabled={loading || !code} className="sd-cta sd-focus w-full h-11 rounded-xl" data-testid="two-factor-submit">
                                {loading ? (<><Loader2 className="mr-1 h-4 w-4 animate-spin" /> Verifying…</>) : `Verify with ${METHOD_LABEL[tab] || 'code'}`}
                            </Button>
                        </form>
                    </Tabs>

                    <div className="mt-4 border-t pt-3 text-xs text-[#1a3a5c]/80">
                        <Link to="/login" className="inline-flex items-center gap-1 hover:text-[#0a1628] hover:underline" data-testid="two-factor-back-to-login">
                            <ArrowLeft className="h-3 w-3" /> Use a different account
                        </Link>
                    </div>
                </Card>
            </div>
            <LegalFooter />
        </div>
    );
}
